import { zonedTimeToUtc } from 'date-fns-tz';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  reduxForm,
  Field,
  Fields,
  FieldArray,
  formValueSelector,
  SubmissionError,
} from 'redux-form/immutable';
import Button from '@material-ui/core/Button';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import FormLabel from '@material-ui/core/FormLabel';
import { type Iterable as ImmutableIterable, type Map as ImmutableMap, Map, List } from 'immutable';
import { FormControlLabel, Radio, RadioGroup, Tooltip } from '@material-ui/core';
import type { FormSubmitHandler, SubmitHandler, FormErrors } from 'redux-form';
import moment from 'moment';

import { scrollToError } from 'utils/scroll.helper';
import FormRadioButton from 'retired/FormRadioButton';
import PhoneNumber from 'retired/PhoneNumberField';
import FormDescriptionRteField from 'retired/FormDescriptionRteField';
import FormDateWithStartAndEndTime from 'retired/FormDateWithStartAndEndTime';
import FormTextField from 'retired/FormTextField';
import FormLocationField from 'retired/FormLocationField';
import FormCheckbox from 'retired/FormCheckbox';
import FormPublishingStatus from 'retired/FormPublishingStatus';
import FormFormsSelector from 'retired/FormFormsSelector';
import FormEditor from 'retired/FormEditor';
import FormPictureSelector from 'retired/FormPictureSelector';
import StepperComponent, { Stepper } from 'retired/Stepper';
import { red } from 'utils/color-styles';
import { fieldArrayHasErrors } from 'utils/reduxForm';
import { PureRender } from 'utils/component-helpers';
import addressToLocation from 'utils/addressToLocation';
import FormLocationSelector from 'retired/FormLocationSelector';
import FormValueSelector from 'retired/FormValueSelector';
import Deed from 'entities/deed/model';
import Form from 'entities/form/model';
import User from 'entities/user/model';
import Campaign from 'entities/campaign/model';
import Organization from 'entities/organization/model';
import { getTimeZoneFromGoogle } from 'utils/getTimeZoneFromGoogle';
import {
  SelectCommunities,
  useCommunitiesList,
  SelectOrganizingCommunity,
} from 'containers/DeedFormPage/CommunitySelect';
import { Features } from 'utils/features';
import type Cause from 'entities/cause/model';
import FormSelectedAndOptedInCompanies from 'retired/FormSelectedAndOptedInCompanies';
import { OrganizationType } from 'generated/graphql';
import { Partner } from 'entities/partners/api';
import { useQueryParams } from 'utils/useQueryParams';

import { DeedFormPageActions } from '../actions';
import { FORM_IDENTIFIER } from '../constants';
import { DisableWaitlistCheckbox } from '../DisableWaitlistCheckbox';
import { RegistrationDeadline } from '../RegistrationDeadline';
import { shareTooltip } from '../copy';
import {
  isAddress,
  isEmail,
  isNumber,
  isPhone,
  isURL,
  maxLength5000,
  notEmpty,
  required,
  requiredRadioButton,
} from '../validators';
import { EventDurationText } from '../EventDurationText';
import LongEventWarningModal from '../LongEventWarningModal';

interface ErrorsProps {
  location: object;
  externalLink: string;
  attendeeLimit: number;
  name: string;
  description: string;
  pictures: string;
  startingAt: Date;
  endingAt: Date;
  registrationEndingAt: Date;
  organizerName: string;
  organizerEmail: string;
  organizerPhone: string;
  formQuestions: [];
  forms: [];
  companyOrPublic: boolean;
}

export interface DeedEventFormProps {
  actions: DeedFormPageActions;
  campaigns: ImmutableIterable<string, Campaign>;
  ERGs: ImmutableIterable<string, Cause>;
  deed: Deed;
  deedType: string;
  endingAt: object;
  errors: FormErrors<ErrorsProps, string>;
  forms: Form[];
  sharedWithCompanies: List<string>;
  handleSubmit: SubmitHandler<ImmutableMap<FormData, string>>;
  locations: object;
  organization: Organization;
  past: boolean;
  pictures: object;
  selectedPartner: string;
  partners: Partner[];
  selectedPictures: object;
  startingAt: object;
  status: string;
  uploadedFile: string;
  user: User;
  virtual: boolean;
  change: (fieldName: string, value: any) => void;
  id: string;
  companyOrPublic: string;
}

const DeedEventForm = ({
  actions,
  campaigns,
  deed,
  deedType,
  startingAt,
  endingAt,
  errors,
  ERGs,
  forms,
  sharedWithCompanies,
  handleSubmit,
  locations,
  organization,
  past,
  pictures,
  selectedPartner,
  partners,
  selectedPictures,
  status = 'draft',
  uploadedFile,
  user,
  virtual,
  change,
  id,
  companyOrPublic,
}: DeedEventFormProps): JSX.Element => {
  const { communities } = useCommunitiesList();
  const [timeZone, setTimeZone] = useState<string | undefined>(undefined);
  const [eventDuration, setEventDuration] = useState<number | null>(0);
  const [warningModalOpen, setWarningModalOpen] = useState<boolean>(false);
  const [pendingSubmission, setPendingSubmission] = useState<any>(null);

  const queryParams = useQueryParams();
  const duplicate = queryParams.get('duplicate');

  let ensureCompanyOnlyDeeds = false;

  const isCommunityFeatureEnabled = user?.hasFeature(Features.COMMUNITIES);

  const deedApprovalRequired =
    partners?.get(selectedPartner)?.deedApprovalRequired &&
    user?.organization?.type === OrganizationType.Nonprofit;

  useEffect(() => {
    ensureCompanyOnlyDeeds = organization?.hasSetting('ensureCompanyOnlyDeeds');
    if (ensureCompanyOnlyDeeds && (id === 'new' || id === 'past')) {
      change('requireAuthentication', true);
      change('public', false);
    }
  }, [organization]);

  const handleLocationSelect = (address: string): React.SetStateAction<object> =>
    addressToLocation(address).subscribe(({ location }) => {
      getTimeZoneFromGoogle(location).subscribe((result) => {
        if (!result) {
          throw Error('Error getting timezone data');
        }
        setTimeZone(result.timeZoneId);
      });
    });

  useEffect(() => {
    if (ensureCompanyOnlyDeeds) {
      change('requireAuthentication', true);
      change('public', false);
    }
  }, [ensureCompanyOnlyDeeds]);

  useEffect(() => {
    if (deed?.timeZone) {
      setTimeZone(deed.timeZone);
    }
  }, [deed]);

  const enableValidation = status !== 'draft';
  let statusMessage: string;
  if (status === 'draft') {
    statusMessage = 'Save draft';
  } else if (status === 'pending') {
    statusMessage = 'Submit for review';
  } else {
    statusMessage = 'Submit';
  }

  const invalid = Boolean(
    (Map.isMap(errors) ? errors.size !== 0 : Object.keys(errors).length) ||
      fieldArrayHasErrors(errors.formQuestions)
  );

  useEffect(() => {
    if (endingAt && startingAt) {
      setEventDuration(moment(endingAt).diff(moment(startingAt), 'hours'));
    }
  }, [endingAt, startingAt]);

  const submitForm = (formData: ImmutableMap<FormData, string>) => {
    // @NOTE-EV: we can't use ReduxForm onSubmitFail prop since combining it with persistentSubmitErrors breaks its behavior
    if (status !== 'draft' && invalid) {
      scrollToError(errors);
      // @NOTE-EV: needs to be thrown manually because FieldArray relies on `submitFail` bool to display error message when the user hasn't interacted with the section.
      throw new SubmissionError({});
    } else {
      const data = formData.toJS();
      actions.saveEventAction({ ...data, manual: past, deedType, timeZone });
    }
  };

  const submit: FormSubmitHandler<ImmutableMap<FormData, string>> = (formData) => {
    if ((eventDuration ?? 0) >= 12 && !warningModalOpen) {
      setPendingSubmission(formData);
      setWarningModalOpen(true);
      return;
    }

    submitForm(formData);
  };

  const handleContinueSubmit = () => {
    setWarningModalOpen(false);
    if (pendingSubmission) {
      submitForm(pendingSubmission);
      setPendingSubmission(null);
    }
  };

  const handleCancelSubmit = () => {
    setWarningModalOpen(false);
    setPendingSubmission(null);
    // Scroll to time settings section
    const timeSettingsElement = document.querySelector(
      '[data-step="Event Date and Time Information *"]'
    );

    if (timeSettingsElement) {
      const headerHeight = 80;

      timeSettingsElement.scrollIntoView({ behavior: 'smooth' });

      // Then after a small delay, adjust for header height
      setTimeout(() => {
        const elementPosition = timeSettingsElement.getBoundingClientRect().top;
        const offsetPosition = elementPosition - headerHeight;

        window.scrollBy({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }, 100);
    }
  };

  const isCompany = organization?.isCompany();
  const isAdmin = user?.type === 'Admin';
  const partnerERGs = ERGs.filter(
    (ERG) =>
      !!ERG &&
      !isCommunityFeatureEnabled &&
      (isCompany || ERG.organization === selectedPartner || (isAdmin && ERG.individualsOnly))
  );

  const initialSharedWithCompanies = List(
    user?.organization?.siblingSubsidiaries?.map((company) => company.id).filter(Boolean) || []
  );

  const showCampaignsAndERGs =
    (isAdmin || isCompany || selectedPartner) &&
    (campaigns.size || partnerERGs.size || communities.size);

  const publicToolTip =
    "Selecting 'Public' will enable any individual to view this event, users will still be required to login to register. Additionally, this event will be publicly available to all Deed users.";
  const companyToolTip = isCompany
    ? "Selecting 'Company Only' will require users to login to verify they are company employees before they can view or register for this event."
    : "Selecting 'Company Only' will require users to login to verify they are company employees of the Partner Company selected above before they can view or register for this event.";

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Stepper orientation="vertical">
        <StepperComponent labelText="Event Location *" componentIndex={0}>
          <Field
            name="virtual"
            component={FormCheckbox}
            label="This event will be hosted virtually"
          />
          {virtual && (
            <Field
              name="externalLink"
              component={FormTextField}
              label="Enter event meeting link (e.g. Zoom, Google Meet, Skype etc.) *"
              fullWidth
              validate={[required, isURL]}
            />
          )}
          {!virtual && (
            <Field
              name="location"
              autoFocus
              component={FormLocationField}
              fullWidth
              label="Where is your Event located? *"
              helperText="Please enter a full address"
              autoComplete="off"
              validate={[required, isAddress]}
              afterSelect={handleLocationSelect}
              locations={user?.locations}
              sx={{ textTransform: 'uppercase' }}
            />
          )}
          <FormControl style={{ width: '100%', height: 20, marginTop: 30 }}>
            <InputLabel shrink>
              If this event should be available only to users from a specific location, select the
              location here:
            </InputLabel>
          </FormControl>
          <Field
            name="allowedLocations"
            component={FormLocationSelector}
            props={{ locations, expanded: false }}
            style={{ marginRight: 10, marginBottom: 10 }}
            fullWidth
          />
        </StepperComponent>
        <div data-step="Event Date and Time Information *" />
        <StepperComponent labelText="Event Date and Time Information *" componentIndex={1}>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>Event Date, Start and End Time *</InputLabel>
          </FormControl>
          {(errors.startingAt || errors.endingAt) && (
            <input
              disabled
              name={errors.startingAt ? 'startingAt' : 'endingAt'}
              style={{ height: 0, width: 0, display: 'block' }}
            />
          )}
          <Fields
            names={['startingAt', 'endingAt']}
            isPreviousDateDisabled
            component={FormDateWithStartAndEndTime}
            timeZone={timeZone}
            setTimeZone={setTimeZone}
            timeZoneDisabled={!(virtual || (!virtual && !timeZone))}
            validate={{
              startingAt: required,
              endingAt: required,
            }}
          />
          {timeZone && zonedTimeToUtc(endingAt?.toDate?.() ?? endingAt, timeZone) < new Date() && (
            <FormLabel error style={{ fontSize: 12, marginTop: 5 }}>
              Invalid time: Date/time in past
            </FormLabel>
          )}
          <EventDurationText eventDuration={eventDuration} />
          <RegistrationDeadline isNew={id === 'new'} timeZone={timeZone} endingAt={endingAt} />
        </StepperComponent>
        <StepperComponent labelText="Event Name *" componentIndex={2}>
          <Field
            name="name"
            component={FormTextField}
            fullWidth
            inputProps={{ maxLength: 100, autoComplete: 'off' }}
            label="What would you like to name your event? *"
            helperText="Choose an exciting name for your event! (40 characters max)"
            validate={required}
            autoComplete="off"
          />
        </StepperComponent>
        <StepperComponent labelText="Event Description" componentIndex={3}>
          <Field
            name="description"
            component={FormDescriptionRteField}
            fullWidth
            multiline
            inputProps={{ maxLength: 5000, autoComplete: 'off' }}
            label="How would you describe your event?"
            helperText="Give a description for your event! (5000 characters max)"
            autoComplete="off"
            validate={maxLength5000}
          />
        </StepperComponent>
        <StepperComponent labelText="Event Point of Contact *" componentIndex={4}>
          <FormControl style={{ width: '100%', height: 17 }}>
            <InputLabel shrink>
              Please provide a point of contact for this Deed who will serve as a liaison for
              registered attendees
            </InputLabel>
          </FormControl>
          <Grid container spacing={3}>
            <Grid item md>
              <Field
                id="organizerName"
                name="organizerName"
                component={FormTextField}
                fullWidth
                label="Name *"
                validate={required}
                autoComplete="off"
              />
            </Grid>
            <Grid item md>
              <Field
                id="organizerEmail"
                name="organizerEmail"
                type="email"
                component={FormTextField}
                fullWidth
                label="Email *"
                validate={[required, isEmail]}
                autoComplete="off"
              />
            </Grid>
            <Grid item md>
              <Field
                id="organizerPhone"
                name="organizerPhone"
                type="tel"
                component={PhoneNumber}
                fullWidth
                label="Phone Number"
                validate={isPhone}
                autoComplete="off"
              />
            </Grid>
          </Grid>
        </StepperComponent>

        {showCampaignsAndERGs ? (
          <StepperComponent
            labelText={`${!isCompany ? 'Partner ' : 'Campaigns, '}ERGs`}
            componentIndex={5}
          >
            <SelectOrganizingCommunity communities={communities} />
            {campaigns.size > 0 && (
              <>
                <FieldArray
                  name="campaigns"
                  component={FormValueSelector}
                  selectMultiple
                  options={campaigns}
                  labelText="Include in campaign"
                />
                <br />
                <br />
              </>
            )}
            {partnerERGs.size > 0 && (
              <>
                <FieldArray
                  name="ERGs"
                  component={FormValueSelector}
                  selectMultiple
                  options={partnerERGs}
                  labelText="Which ERGs match your Event? (you may choose more than one)"
                />
              </>
            )}
            <SelectCommunities communities={communities} />
          </StepperComponent>
        ) : null}

        <StepperComponent labelText="Event Picture *" componentIndex={showCampaignsAndERGs ? 6 : 5}>
          <FieldArray
            name="pictures"
            component={FormPictureSelector}
            options={pictures}
            pictures={selectedPictures}
            fileUploadedAction={actions.fileUploadedAction}
            uploadedFile={uploadedFile}
            labelText="Choose a photo for your event *"
            validate={notEmpty}
          />
        </StepperComponent>
        <StepperComponent
          labelText="Attendee Information *"
          componentIndex={showCampaignsAndERGs ? 7 : 6}
        >
          <Field
            name="mustKnows"
            component={FormTextField}
            inputProps={{ maxLength: 500 }}
            fullWidth
            multiline
            label="What are the must knows?"
            helperText="Please provide any essential information that attendees should be aware of"
            autoComplete="off"
          />
          <Field
            name="attendeeLimit"
            component={FormTextField}
            type="number"
            inputProps={{ min: '0', step: 1 }}
            fullWidth
            label="How many people do you expect to join? *"
            helperText="Overestimate! It's good to enter about 20% more than what you need."
            autoComplete="off"
            validate={[required, isNumber]}
          />
          <Field
            name="waitlistLimit"
            component={DisableWaitlistCheckbox}
            label="Disable waitlist"
          />
        </StepperComponent>
        <StepperComponent
          labelText="Additional Event Settings"
          componentIndex={showCampaignsAndERGs ? 8 : 7}
        >
          <Field
            name="linkOnly"
            component={FormCheckbox}
            label="Link only: only users with access to the link can view"
          />
        </StepperComponent>
        <StepperComponent labelText="Attendee Forms" componentIndex={showCampaignsAndERGs ? 9 : 8}>
          <FieldArray
            name="forms"
            component={FormFormsSelector}
            options={forms}
            labelText="Choose the forms which would be required for an attendee to fill in. Go to the Forms tab to create new forms."
          />
          {organization?.parent && (
            <Field
              name="shareFormResponses"
              component={FormCheckbox}
              label="Allow form responses to be visible to other organizations"
            />
          )}
        </StepperComponent>
        <StepperComponent
          labelText="Additional Attendee Questions"
          componentIndex={showCampaignsAndERGs ? 10 : 9}
        >
          <FieldArray name="formQuestions" component={FormEditor} change={change} />
        </StepperComponent>

        {(!ensureCompanyOnlyDeeds || Boolean(user?.organization?.parent)) && (
          <StepperComponent
            labelText="Deed Visibility *"
            componentIndex={showCampaignsAndERGs ? 10 : 9}
          >
            <Field
              name="companyOrPublic"
              component={FormRadioButton}
              validate={requiredRadioButton}
            >
              <RadioGroup row>
                <Tooltip title={companyToolTip}>
                  <FormControlLabel
                    control={<Radio />}
                    value="company"
                    label="Company Only"
                    checked={companyOrPublic === 'company'}
                    disabled={!isCompany && !selectedPartner}
                  />
                </Tooltip>
                <Tooltip title={publicToolTip}>
                  <FormControlLabel
                    control={<Radio />}
                    value="public"
                    label="Public"
                    checked={companyOrPublic === 'public'}
                    disabled
                  />
                </Tooltip>
                {user?.organization?.parent && (
                  <Tooltip title={shareTooltip}>
                    <FormControlLabel
                      control={<Radio />}
                      value="shared"
                      label="Share with other Organizations"
                      checked={companyOrPublic === 'shared'}
                      disabled={!isCompany}
                    />
                  </Tooltip>
                )}
              </RadioGroup>
            </Field>
            {isCompany && user?.organization?.parent && companyOrPublic === 'shared' && (
              <Field
                name="sharedWithCompanies"
                component={FormSelectedAndOptedInCompanies}
                optedInCompanies={duplicate ? List() : deed?.optedInCompanies}
                sharedWithCompanies={sharedWithCompanies || initialSharedWithCompanies}
                validate={notEmpty}
              />
            )}
          </StepperComponent>
        )}

        <StepperComponent
          labelText="Review"
          componentIndex={showCampaignsAndERGs ? 11 : 10}
          currentStep={10}
          finalStep
        >
          <div>
            <p>
              {enableValidation && invalid ? (
                <span style={{ color: red }}>
                  Uh oh – there is missing information that is important for attendees to know!
                  Before you are able to publish this deed, you&apos; need to finish completing the
                  form. For now, your deed can be saved as a draft.
                </span>
              ) : (
                <>
                  Take time to review the information you entered. The deed can be set to pending or
                  published only when all mandatory sections have been filled correctly.
                </>
              )}
            </p>
            <br />
            <br />
            <Field
              name="status"
              component={FormPublishingStatus}
              label="Publishing status:"
              deedApprovalRequired={deedApprovalRequired}
            />
            <br />
            <br />
            <Button type="submit" color="primary" variant="contained">
              {statusMessage}
            </Button>
          </div>
        </StepperComponent>
      </Stepper>
      <LongEventWarningModal
        open={warningModalOpen}
        onEdit={handleCancelSubmit}
        onContinue={handleContinueSubmit}
      />
    </form>
  );
};

const selector = formValueSelector(FORM_IDENTIFIER);
export default connect((state) => ({
  virtual: selector(state, 'virtual'),
  mainPicture: selector(state, 'mainPicture'),
  selectedPictures: selector(state, 'pictures'),
  name: selector(state, 'name'),
  deedId: selector(state, 'id'),
  endingAt: selector(state, 'endingAt'),
  startingAt: selector(state, 'startingAt'),
  location: selector(state, 'location'),
  duties: selector(state, 'duties'),
  attendeeLimit: selector(state, 'attendeeLimit'),
  id: selector(state, 'id'),
  description: selector(state, 'description'),
  companyOrPublic: selector(state, 'companyOrPublic'),
  sharedWithCompanies: selector(state, 'sharedWithCompanies'),
}))(
  reduxForm({
    form: FORM_IDENTIFIER,
    enableReinitialize: true,
    keepDirtyOnReinitialize: true,
    persistentSubmitErrors: true,
  })(PureRender(DeedEventForm))
);
